/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, makeStateKey, TransferState } from '@angular/core';

import { v4 as uuidV4 } from 'uuid';

import {
  Observable,
  from,
  of,
  map,
  take,
  switchMap,
  catchError,
  shareReplay,
  timeout,
} from 'rxjs';

import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';

import { AnalyticsBrowser } from '@segment/analytics-next';
import { ConfigurationService, IAmplitudeAppConfig } from '@sondermind/configuration';
import { CurrentUser } from '@sondermind/data-access/client-user';
import {
  IFeatureFlagService,
  EvaluationContext,
  FeatureFlagVariant,
  IEvaluationOptions,
  FeatureFlagKey
} from '@sondermind/data-access/feature-flags';
import { SentryClient } from '@sondermind/sentry-client';
import { Logger } from '@sondermind/datadog-logging';

// Amplitude FF service setup according to SDK guide:
// https://www.docs.developers.amplitude.com/experiment/sdks/javascript-sdk/#configuration

// Default timeout for fetch requests
const DEFAULT_TIMEOUT = 3000;

@Injectable({
  providedIn: 'root'
})
export class AmplitudeFeatureFlagService implements IFeatureFlagService {
  private experimentClient?: ExperimentClient;
  private analytics?: AnalyticsBrowser;
  private initialized = false;
  private deviceId: string;
  private anonymousId: string;

  constructor(
    private configurationService: ConfigurationService<IAmplitudeAppConfig>,
    private currentUser: CurrentUser,
    private transferState: TransferState,
    private logger: Logger,
  ) { }

  initialize$(): Observable<boolean> {
    if (this.initialized) {
      return of(true);
    }

    const config = this.configurationService.env.app;
    if (!config?.amplitudeDeploymentKey) {
      return of(false);
    }

    // uncomment debug property for amplitude console logging
    this.experimentClient = Experiment.initialize(config.amplitudeDeploymentKey, {
      // debug: this.configurationService.env.sentryDebug,
      serverUrl: 'https://amplitude.sondermind.com',
      flagsServerUrl: 'https://flag.sondermind.com',
      automaticExposureTracking: false,
      exposureTrackingProvider: {
        track: (exposure) => {
          this.trackExposureEvent(exposure);
        }
      }
    });

    return this.anonymousId$().pipe(
      switchMap((anonymousId) => {
        // Storing the anonymousId as it might be needed later (by consumers this service)
        this.anonymousId = anonymousId;
        this.deviceId = anonymousId;
        this.initialized = true;
        return of(true);
      }),
      catchError(
        (error) => {
          SentryClient.captureException(error);
          return of(false);
        }
      )
    );
  }

  evaluate$<TKey extends string = string, TPayload = unknown>(
    flagKey: FeatureFlagKey,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    // Check if the feature flag is already cached in the transfer state
    const key = makeStateKey<FeatureFlagVariant<TKey, TPayload>>(flagKey);
    if (this.transferState.hasKey(key)) {
      return of(this.transferState.get(
        key,
        { key: 'off', payload: defaultValue } as FeatureFlagVariant<TKey, TPayload>)
      );
    }

    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) {
          return of(null);
        }

        return (this.currentUser.user$ ?? of({ platformAccountUuid: 'anonymous' })).pipe(
          take(1),
          switchMap((currentUser) => {
            const amplitudeUser = {
              device_id: this.deviceId
            };

            if (!!currentUser?.platformAccountUuid && currentUser?.platformAccountUuid !== 'anonymous') {
              amplitudeUser['user_id'] = currentUser?.platformAccountUuid;
            }

            return from(this.experimentClient.fetch(amplitudeUser)).pipe(
              switchMap(() => this.evaluateVariant$<TKey, TPayload>(flagKey, defaultValue)),
              timeout(options?.timeout ?? DEFAULT_TIMEOUT)
            );
          })
        );
      }),
      catchError((error: unknown) => {
        SentryClient.captureException(error);
        this.logger.error(`Error evaluating feature flag ${flagKey}. Returning null.`, { error });
        return of(null);
      }),
      shareReplay(1)
    );
  }

  /**
   * Returns an observable that emits the boolean value of the feature flag.
   *
   * @param flagKey - The key of the feature flag to evaluate.
   * @param defaultValue - The default value to return if the feature flag is not found or cannot be evaluated.
   * @returns An observable that emits the boolean value of the feature flag.
   *
   * Note: `false` is the implicit default value
   */
  isEnabled$(flagKey: FeatureFlagKey, defaultValue = false): Observable<boolean> {
    return this.evaluate$<string, boolean>(flagKey, defaultValue).pipe(
      map((variant) => ['on', 'true', 'enabled'].includes(variant?.key ?? ''))
    );
  }

  private evaluateVariant$<TKey extends string = string, TPayload = unknown>(
    flagKey: string,
    defaultValue: TPayload
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    if (!this.experimentClient) {
      this.logger.warn(`Experiment client not initialized. Returning null.`, { flagKey });
      return of(null);
    }

    try {
      const variant = this.experimentClient.variant(flagKey);

      if (!this.isValidVariant(variant)) {
        this.logger.warn(`Variant not found for flag ${flagKey}. Returning null.`, { flagKey });
        return of(null);
      }

      // If experiment is off, return default value or null
      if (this.isExperimentOff(variant)) {
        return of(defaultValue ? this.createDefaultVariant<TKey, TPayload>(defaultValue) : null);
      }

      // Don't track exposure here - let the consumer call trackExposure$ when actually exposed
      return of({
        key: variant.key as TKey,
        payload: variant.payload as TPayload
      });
    } catch (error: unknown) {
      SentryClient.captureException(error);
      this.logger.error(
        `Error evaluating variant for flag ${flagKey}. Returning null.`,
        { error, deviceId: this.deviceId }
      );
      return of(null);
    }
  }

  getVariant$<TKey extends string = string, TPayload = unknown>(
    context: EvaluationContext,
    flagKey: string,
    defaultValue: TPayload = null as TPayload
  ): Observable<FeatureFlagVariant<TKey, TPayload> | null> {
    return this.getVariantsForUser$(context.accountId).pipe(
      map((variants) => {
        const variant = variants[flagKey];

        if (!this.isValidVariant(variant)) {
          return null;
        }

        if (this.isExperimentOff(variant)) {
          return defaultValue ? this.createDefaultVariant<TKey, TPayload>(defaultValue) : null;
        }

        // Only track exposure for valid, active experiments
        if (this.experimentClient) {
          this.experimentClient.exposure(flagKey);
        }

        return {
          key: variant.key as TKey,
          payload: variant.payload as TPayload
        };
      }),
      catchError((error: unknown) => {
        SentryClient.captureException(error);
        this.logger.error(
          `Error getting variant for flag ${flagKey}. Returning null.`,
          { error, deviceId: this.deviceId }
        );
        return of(null);
      })
    );
  }

  private getVariantsForUser$(userId: string): Observable<Record<string, FeatureFlagVariant>> {
    return this.initialize$().pipe(
      switchMap((initialized) => {
        if (!initialized || !this.experimentClient) return of({});

        return from(this.experimentClient.fetch({ user_id: userId })).pipe(
          map((variants) => Object.entries(variants).reduce((acc, [key, variant]: [string, FeatureFlagVariant]) => {
            // Skip variants with undefined keys
            if (!variant?.key) return acc;

            acc[key] = {
              key: variant.key,
              payload: variant.payload
            };
            return acc;
          }, {} as Record<string, FeatureFlagVariant>)),
          catchError(() => of({}))
        );
      })
    );
  }

  anonymousId$(): Observable<string> {
    // If the anonymousId is already set, return it
    if (this.anonymousId) {
      return of(this.anonymousId);
    }

    if (!this.configurationService.env.app?.segmentWriteKey) {
      SentryClient.captureMessage('No segment write key was provided for the amplitude feature flag service. ' +
        'Continuing by using a generated device ID');
    } else {
      this.analytics = AnalyticsBrowser.load(
        {
          writeKey: this.configurationService.env.app?.segmentWriteKey,
          cdnURL: 'https://segment.sondermind.com'
        },
        { integrations: { 'Segment.io': { apiHost: 'segment-api.sondermind.com/v1' } } }
      );
    }

    if (!this.analytics) {
      return of(uuidV4());
    }

    return from(this.analytics.user()).pipe(
      switchMap((analyticsUser) => {
        const anonymousId = analyticsUser?.anonymousId();
        return of(anonymousId);
      }),
      catchError((error) => {
        SentryClient.captureException(error);
        return of(uuidV4());
      })
    );
  }

  /**
   * Creates a default variant with 'off' key and the provided payload
   */
  private createDefaultVariant<TKey extends string, TPayload>(payload: TPayload): FeatureFlagVariant<TKey, TPayload> {
    return { key: 'off' as TKey, payload };
  }

  /**
   * Checks if a variant is valid (not null/undefined and has a key)
   */
  private isValidVariant(variant: any): boolean {
    return !!variant?.key;
  }

  /**
   * Checks if the experiment is considered "off" based on the variant key
   */
  private isExperimentOff(variant: any): boolean {
    const offKeys = ['off', 'disabled', 'false'];
    return offKeys.includes(variant?.key?.toLowerCase());
  }

  trackExposure$(flagKey: FeatureFlagKey, variant: FeatureFlagVariant): Observable<void> {
    return this.initialize$().pipe(
      switchMap((initialized) => this.handleExposureTracking(initialized, flagKey, variant)),
      catchError((error: unknown) => this.handleExposureError(error, flagKey, variant))
    );
  }

  private handleExposureTracking(
    initialized: boolean,
    flagKey: FeatureFlagKey,
    variant: FeatureFlagVariant
  ): Observable<void> {
    if (!this.canTrackExposure(initialized)) {
      this.logger.warn(`Cannot track exposure - service not initialized`, { flagKey });
      return of(undefined);
    }

    return this.trackAmplitudeExposure(flagKey, variant);
  }

  private canTrackExposure(initialized: boolean): boolean {
    return initialized && !!this.experimentClient;
  }

  private trackAmplitudeExposure(flagKey: FeatureFlagKey, variant: FeatureFlagVariant): Observable<void> {
    try {
      this.experimentClient.exposure(flagKey);
      this.logger.debug(`Feature flag exposure tracked successfully`, {
        flagKey,
        variant: variant.key
      });
      return of(undefined);
    } catch (error: unknown) {
      return this.handleExposureError(error, flagKey, variant);
    }
  }

  private handleExposureError(
    error: unknown,
    flagKey: FeatureFlagKey,
    variant: FeatureFlagVariant
  ): Observable<void> {
    const errorContext = { error, flagKey, variant: variant.key };
    SentryClient.captureException(error);
    this.logger.error(`Failed to track feature flag exposure`, errorContext);
    return of(undefined);
  }

  private trackExposureEvent(exposure: { flag_key: string; variant?: string; experiment_key?: string; }): void {
    try {
      this.analytics?.track('$exposure', exposure);

      this.logger.debug('Exposure event tracked successfully via analytics', { exposure });
    } catch (error) {
      this.logger.error('Error tracking exposure event via analytics', { error, exposure });
      SentryClient.captureException(error);
    }
  }
}
